import { useState } from 'react';
import { PACKAGES } from '@aslgo/shared';
import { useAppStore } from '../store/useAppStore.js';
import { usePackagePurchase } from '../hooks/usePackagePurchase.js';
import { PurchaseModal } from './PurchaseModal.js';
import { ConfirmPurchaseDialog } from './ConfirmPurchaseDialog.js';
import { EnterpriseContactRow } from './EnterpriseContactRow.js';
import { AlertIcon } from '../icons.js';

const LOW_BALANCE_THRESHOLD = 5;
const CRITICAL_BALANCE_THRESHOLD = 1;

interface Props {
  /** Positioning/layout for the outer container, supplied by CallView so the
   *  pill can float over the stage (chat closed) or stack above the chat rail. */
  className?: string;
}

/**
 * The learner's minute counter. The headline is the minute count with a colour
 * ramp:
 *   healthy ( >5 ) → brand yellow
 *   low     (≤ 5 ) → amber + warning + inline Add Minutes
 *   empty   (= 0 ) → red + "call ending" message + inline Add Minutes
 *
 * Minute consumption is driven by CallView (so the ticker survives this panel
 * being re-placed when chat opens/closes); this component only displays the
 * balance and offers the inline top-up form.
 */
export function SessionMinutesPanel({ className }: Props) {
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  const setSessionMinutes = useAppStore((s) => s.setSessionMinutes);

  const [selected, setSelected] = useState<string | null>(null);
  const {
    card, submitting, error: chargeError, purchase, buy, onPurchased, closePurchase,
    pendingBuy, confirmBuy, cancelBuy,
  } = usePackagePurchase(setSessionMinutes);

  async function onBuy() {
    if (selected == null) return;
    const pkg = PACKAGES.find((p) => p.id === selected)!;
    await buy(pkg);
    setSelected(null);
  }

  const empty = sessionMinutes <= 0;
  const critical = sessionMinutes <= CRITICAL_BALANCE_THRESHOLD;
  const lowBalance = sessionMinutes <= LOW_BALANCE_THRESHOLD;

  // Colour ramp: healthy → low → critical
  const tone = empty || critical
    ? 'bg-status-error/95 text-white ring-status-error/40'
    : lowBalance
      ? 'bg-status-warning/95 text-white ring-status-warning/40'
      : 'bg-brand/95 dark:bg-brand-bright/95 text-ink-900 ring-brand/40';

  const textColor = empty || critical ? 'text-red-50' : lowBalance ? 'text-amber-50' : 'text-ink-900/70';

  return (
    <div className={className}>
      {/* A phone has no room for the full panel over the video, so while the
       *  balance is healthy it collapses to a chip. At or below the low
       *  threshold the full panel returns at every width — the warning and the
       *  inline top-up must not be reduced to a number. */}
      <div
        data-testid="minutes-compact"
        className={`${lowBalance ? 'hidden' : 'md:hidden'} inline-flex items-baseline gap-1
                    rounded-full px-2.5 py-1 backdrop-blur-md ring-1 shadow-chrome ${tone} animate-fade-in`}
      >
        <span className="font-mono tabular-nums text-[0.95rem] font-semibold leading-none">
          {sessionMinutes}
        </span>
        <span className={`text-[0.6rem] tracking-wide uppercase ${textColor}`}>min</span>
      </div>

      <div
        data-testid="minutes-full"
        className={`${lowBalance ? '' : 'hidden md:block'} rounded-2xl px-4 py-3 backdrop-blur-md ring-1 shadow-chrome ${tone} animate-fade-in`}
      >
        <div className="flex items-center justify-center gap-3">
          {empty || critical ? <AlertIcon /> : null}
          <span className="font-mono tabular-nums text-2xl font-semibold leading-none">
            {sessionMinutes}
          </span>
          <span className={`text-[0.78rem] tracking-wide uppercase ${textColor}`}>
            min balance
          </span>
        </div>

        {empty ? (
          <p className="mt-2 text-[0.85rem] text-center font-medium">
            Out of minutes — call ends in 10s. Top up to keep going.
          </p>
        ) : lowBalance ? (
          <p className="mt-2 text-[0.85rem] text-center">
            Running low — top up to extend the session.
          </p>
        ) : null}

        {lowBalance ? (
          <form
            onSubmit={(e) => { e.preventDefault(); void onBuy(); }}
            className="mt-3 flex flex-col gap-2"
          >
            <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto">
              {PACKAGES.map((p) => {
                const isSelected = selected === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected(p.id)}
                    disabled={submitting}
                    className={`flex items-center justify-between gap-2 rounded-lg border p-2 text-left text-[0.85rem] transition
                                disabled:opacity-50 disabled:cursor-not-allowed
                                ${
                                  isSelected
                                    ? 'bg-white text-ink-900 border-white'
                                    : 'bg-white/10 border-white/15 text-white hover:bg-white/15'
                                }`}
                  >
                    <span>
                      <span className="font-semibold">{p.name}</span>
                      <span className={`block text-[0.75rem] ${isSelected ? 'text-ink-500' : 'text-white/80'}`}>
                        {p.credits} cr · {p.minutes} min
                      </span>
                    </span>
                    <span className="font-semibold whitespace-nowrap">${p.priceDollars.toLocaleString()}</span>
                  </button>
                );
              })}
              <EnterpriseContactRow tone="dark" compact />
            </div>
            <button
              type="submit"
              disabled={submitting || selected == null}
              className="px-3.5 py-2 rounded-lg bg-white text-ink-900 text-[0.85rem] font-medium
                         hover:bg-white/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Buying…' : 'Buy package'}
            </button>
          </form>
        ) : null}

        {chargeError ? <p className="mt-2 text-[0.8rem] text-white/95">{chargeError}</p> : null}
      </div>
      {purchase ? (
        <PurchaseModal clientSecret={purchase.clientSecret} onPurchased={onPurchased} onClose={closePurchase} />
      ) : null}
      {pendingBuy && card ? (
        <ConfirmPurchaseDialog pkg={pendingBuy} card={card} busy={submitting} onConfirm={confirmBuy} onCancel={cancelBuy} />
      ) : null}
    </div>
  );
}
